import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AppShell from "@/components/AppShell";
import Onboarding from "@/pages/Onboarding";
import DatasetsList from "@/pages/DatasetsList";
import DatasetView from "@/pages/DatasetView";
import DashboardsList from "@/pages/DashboardsList";
import DashboardView from "@/pages/DashboardView";
import AdminUpload from "@/pages/AdminUpload";
import JoinDatasets from "@/pages/JoinDatasets";
import TeamDirectory from "@/pages/TeamDirectory";
import Members from "@/pages/Members";
import CompanySettings from "@/pages/CompanySettings";
import OrgChart from "@/pages/OrgChart";
import PublicDashboard from "@/pages/PublicDashboard";
import AskPeopleIQ from "@/pages/AskPeopleIQ";
import "@/App.css";

function TenantSlugGuard({ children }) {
  const { slug } = useParams();
  const { company, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!company) return <Navigate to="/login" replace />;
  if (company.slug !== slug) return <Navigate to={`/t/${company.slug}`} replace />;
  return children;
}

function AuthGate({ children }) {
  const { user, company, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user || !company) return <Navigate to="/login" replace />;
  return children;
}

function AdminGate({ children }) {
  const { user } = useAuth();
  if (user?.role !== "company_admin" && user?.role !== "platform_admin") {
    return <Navigate to="../" replace />;
  }
  return children;
}

function FullPageLoader() {
  return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
}

function RootRedirect() {
  const { company, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (company) return <Navigate to={`/t/${company.slug}`} replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/public/d/:token" element={<PublicDashboard />} />

          <Route path="/t/:slug" element={<AuthGate><TenantSlugGuard><AppShell /></TenantSlugGuard></AuthGate>}>
            <Route index element={<Navigate to="ask" replace />} />
            <Route path="ask" element={<AskPeopleIQ />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="datasets" element={<DatasetsList />} />
            <Route path="datasets/:id" element={<DatasetView />} />
            <Route path="dashboards" element={<DashboardsList />} />
            <Route path="dashboards/:id" element={<DashboardView />} />
            <Route path="orgchart" element={<OrgChart />} />
            <Route path="upload" element={<AdminGate><AdminUpload /></AdminGate>} />
            <Route path="join" element={<AdminGate><JoinDatasets /></AdminGate>} />
            <Route path="team" element={<AdminGate><TeamDirectory /></AdminGate>} />
            <Route path="members" element={<AdminGate><Members /></AdminGate>} />
            <Route path="settings" element={<AdminGate><CompanySettings /></AdminGate>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
  );
}

export default App;
